import type { DriveBrowseScopeKind } from './drive-browse-events'

export type DriveFolderJump = {
  viewKind: DriveBrowseScopeKind | 'sharedDrive'
  folderId?: string
  driveId?: string
  driveName?: string
  /** Select this row in main without entering it (Preview). */
  selectId?: string
  openPreview?: boolean
}

export type DriveJumpFolder = {
  id: string
  name: string
  webViewLink?: string
  /** Set when this node lives in a shared drive (root: id === driveId). */
  driveId?: string
  driveName?: string
  parentId?: string
}

export function driveExternalFolderUrl(id: string): string {
  return `https://drive.google.com/drive/folders/${encodeURIComponent(id)}`
}

export function driveExternalRootUrl(
  kind: 'myDrive' | 'sharedDrives' | 'recent',
): string {
  if (kind === 'myDrive') return 'https://drive.google.com/drive/my-drive'
  if (kind === 'recent') return 'https://drive.google.com/drive/recent'
  return 'https://drive.google.com/drive/shared-drives'
}

export function jumpFolderBrowseQuery(
  workspaceId: string,
  folder: DriveJumpFolder,
): URLSearchParams {
  const params = new URLSearchParams({
    organizationId: workspaceId,
    foldersOnly: '1',
  })
  if (folder.driveId) {
    params.set('corpus', 'drive')
    params.set('driveId', folder.driveId)
    if (folder.id !== folder.driveId) params.set('folderId', folder.id)
    return params
  }
  params.set('corpus', 'myDrive')
  params.set('folderId', folder.id)
  return params
}

/** Enter this folder in the main Drive list. */
export function jumpToOpenFolder(folder: DriveJumpFolder): DriveFolderJump {
  if (folder.driveId) {
    if (folder.id === folder.driveId) {
      return {
        viewKind: 'sharedDrive',
        driveId: folder.driveId,
        driveName: folder.driveName,
      }
    }
    return {
      viewKind: 'sharedDrive',
      driveId: folder.driveId,
      driveName: folder.driveName,
      folderId: folder.id,
    }
  }
  return { viewKind: 'myDrive', folderId: folder.id }
}

/** Show this folder as a selected row (parent list) and pin preview. */
export function jumpToPreviewFolder(folder: DriveJumpFolder): DriveFolderJump {
  if (folder.driveId) {
    if (folder.id === folder.driveId) {
      return {
        viewKind: 'sharedDrives',
        selectId: folder.id,
        openPreview: true,
      }
    }
    const atDriveRoot = !folder.parentId || folder.parentId === folder.driveId
    return {
      viewKind: 'sharedDrive',
      driveId: folder.driveId,
      driveName: folder.driveName,
      folderId: atDriveRoot ? undefined : folder.parentId,
      selectId: folder.id,
      openPreview: true,
    }
  }
  return {
    viewKind: 'myDrive',
    folderId: folder.parentId,
    selectId: folder.id,
    openPreview: true,
  }
}
